import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { useUIStore } from '@/state/uiStore';
import { createAndPayAtDesk, type DeskCreateAndPayResult } from '@/domain/depositFlow';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { createWalkInIntent, intentFailureCopy } from '@/api/intents';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import { matchCustomer, matchOrCreateCustomer } from '@/domain/selfBookFlow';
import { sessionLengthForDuration, depositForDuration } from '@/domain/pricing';
import type { Booking, CustomerRecord, DepositChannel, StudioId } from '@/domain/types';
import { startNoEarlierThanNow } from '@/utils/slotClip';

/**
 * Walk-in gap-first flow (tv-pq6, Diagram 07). Two greenfield paths for a person
 * who walks into the shop:
 *  - a gap today that suits -> seatWalkInNow: tattoo today, NO deposit, they pay
 *    at checkout after the session.
 *  - no gap today -> bookWalkInFuture: book a future slot and take the deposit in
 *    the shop now.
 *
 * The gap discovery itself is read-only (buildGapList / buildDaySchedule) and the
 * picking UI is design-gated; these flows take an already-chosen slot. "Now" is
 * always the sim clock.
 */

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/** The studio the walk-in is standing in: the artist's studio, else the named
 *  customer's home studio, else the first studio on the books. */
function studioForWalkIn(artistId: string, customerId?: string): StudioId {
  const brain = useBrainStore.getState();
  const fromArtist = brain.artists[artistId]?.studioId;
  if (fromArtist) return fromArtist;
  const fromCustomer = customerId ? brain.customers[customerId]?.primaryStudioId : undefined;
  if (fromCustomer) return fromCustomer;
  return Object.keys(brain.studios)[0] as StudioId;
}

/** Contact captured at the front desk. Phone is required by the intake UI (it
 *  goes on file for the SMS thread); name + email are best-effort. */
export interface WalkInContact {
  name?: string;
  phone?: string;
  email?: string;
}

/**
 * Create-or-find the walk-in's customer record with the captured PHONE ON FILE,
 * so reminders / the waiver / a deposit link have a number to reach. Delegates
 * to the house intake dedupe (matchOrCreateCustomer): a returning walk-in whose
 * strong ID (phone, email, or Instagram handle) matches reuses their card instead
 * of spawning a duplicate — a shared name alone never merges two people. Shared
 * entry point - the future-date deposit path resolves the
 * customer HERE before DepositLinkModal opens, and seatWalkInNow uses it for the
 * today path - so the mutation stays in the flow layer, not the component.
 */
export function findOrCreateWalkInCustomer(contact: WalkInContact, studioId: StudioId): string {
  return matchOrCreateCustomer({
    name: contact.name?.trim() || 'Walk-in',
    email: contact.email?.trim() ?? '',
    mobile: contact.phone?.trim() ?? '',
    studioId,
  });
}

export interface WalkInNowInput extends WalkInContact {
  customerId?: string;
  artistId: string;
  startIso: string;
  durationMin: number;
  /** Free-text "what they want", captured at the desk. Falls back to a generic brief. */
  designBrief?: string;
  onConfirmed?: (booking: Booking) => void;
  onFailed?: () => void;
}

/**
 * Seat a walk-in now with NO deposit (Diagram 07). Creates a `confirmed` booking
 * so the floor flow can check them in / seat them and checkout collects the full
 * payment afterwards. `depositWaived` marks it as a deliberate no-deposit walk-in
 * (money down is collected at the make-sale), not an unpaid hold. No path seats a
 * booking without a deposit today - this is the greenfield one.
 */
export function seatWalkInNow(input: WalkInNowInput): Booking {
  const brain = useBrainStore.getState();
  const at = nowIso();
  // Never seated into hours already past (tv-2na.10.3): a stale start moves to
  // the next quarter hour after the sim clock.
  const startIso = startNoEarlierThanNow(input.startIso, at);
  const studioId = studioForWalkIn(input.artistId, input.customerId);
  const resolved = input.customerId ? undefined : draftWalkInCustomer(input, studioId);
  const customerId = input.customerId ?? resolved!.customer.id;
  const durationMin = Math.max(15, Math.round(input.durationMin));
  const bookingId = `bk-walkin-${customerId}-${new Date(startIso).getTime()}`;

  const booking: Booking = {
    id: bookingId,
    customerId,
    studioId,
    artistId: input.artistId,
    start: startIso,
    durationMin,
    sessionLength: sessionLengthForDuration(durationMin),
    status: 'confirmed',
    confirmedAt: at,
    // No deposit down: a walk-in pays in full at checkout (Diagram 07). Flagged so
    // a missing-deposit surface reads it as a walk-in, not an unpaid booking.
    depositWaived: true,
    designBrief: input.designBrief?.trim() || 'Walk-in - seated on the day.',
    referencePhotos: [],
    source: 'in-studio-walkin',
    createdAt: at,
  };
  const confirm = () => {
    const mirror = () => {
      if (resolved?.isNew) brain.upsertCustomer({...resolved.customer, leadStage: 'booked'});
      brain.upsertBooking(booking);
    };
    if (isStoreFallbackEnabled()) mirror();
    else withServerAuthoredWrites([
      ['bookings', bookingId],
      ...(resolved?.isNew ? [['customers', customerId] as const] : []),
    ], mirror);
    brain.appendActivity({
      id: `act-walkin-seat-${bookingId}`, type: 'booking-created', customerId, studioId,
      artistId: input.artistId, body: `Walk-in seated with ${input.artistId}. No deposit; pays at checkout.`, timestamp: at,
    });
    eventBus.publish('walkin:seated', { bookingId, artistId: input.artistId });
    input.onConfirmed?.(booking);
  };
  if (isStoreFallbackEnabled()) confirm();
  else void createWalkInIntent(booking, resolved?.isNew ? resolved.customer : undefined).then(result => {
    if (result.ok) confirm();
    else {
      const copy = intentFailureCopy(result);
      useUIStore.getState().pushNotification({intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 8000});
      input.onFailed?.();
    }
  });
  return booking;
}

export interface WalkInFutureInput extends WalkInContact {
  customerId?: string;
  artistId: string;
  startIso: string;
  durationMin: number;
  method: 'eftpos' | 'cash' | 'bank-transfer';
}

export interface WalkInFutureResult {
  bookingId: string;
  depositId: string;
  /** Settles when the server answers the create-and-pay (failure already toasted). */
  settled: Promise<DeskCreateAndPayResult>;
}

/** Where an in-shop deposit settled, for the ledger's reconciliation channel. */
export const WALKIN_METHOD_CHANNEL: Record<WalkInFutureInput['method'], DepositChannel> = {
  eftpos: 'card',
  cash: 'cash',
  'bank-transfer': 'transfer',
};

/**
 * Resolve the walk-in's customer WITHOUT writing anything (tv-2na.8.2): a strong-ID
 * match (phone / email) reuses the card on file; otherwise a DRAFT record is built
 * (opaque id, captured phone) that is created INSIDE the booking's create-and-pay /
 * hold-slot intent — a pre-intent customer PUT would race the booking's customer
 * FK, and a walk-in who walks off before paying leaves no orphan profile.
 */
export function draftWalkInCustomer(
  contact: WalkInContact,
  studioId: StudioId,
): { customer: CustomerRecord; isNew: boolean } {
  const brain = useBrainStore.getState();
  const m = matchCustomer({ mobile: contact.phone, email: contact.email, name: contact.name });
  if (m.kind === 'matched' && brain.customers[m.id]) return { customer: brain.customers[m.id]!, isNew: false };
  const name = contact.name?.trim() || 'Walk-in';
  const [firstName, ...rest] = name.split(/\s+/);
  const email = (contact.email ?? '').trim().toLowerCase();
  const customer: CustomerRecord = {
    id: `cust-${crypto.randomUUID().slice(0, 8)}`,
    firstName: firstName ?? name,
    lastName: rest.join(' '),
    phone: (contact.phone ?? '').trim(),
    ...(email ? { email } : {}),
    ageBand: '25-34',
    suburb: '', postcode: '', state: 'VIC',
    distanceToNearestStudioKm: 0,
    nearestStudioId: studioId,
    primaryStudioId: studioId,
    source: { channel: 'walk-in', firstTouchAt: nowIso() },
    leadScore: 50, leadStage: 'deposit-pending', toneTag: 'casual-young',
    engagementStatus: 'active',
    conversation: [], rapportNotes: [], futureReadinessSignals: [],
    bookings: [], pieces: [], deposits: [],
    creditOnFile: { balanceAud: 0, history: [] },
    crossStudioTouches: [], doNotContact: false, lifetimeSpendAud: 0,
  };
  return { customer, isNew: true };
}

/**
 * Book a walk-in a FUTURE slot with the deposit taken in the shop now (Diagram
 * 07, the "no gap today" branch; the console's walkin-book-future). The
 * customer (if new), the confirmed booking and the paid deposit land in ONE
 * create-and-pay intent (createAndPayAtDesk); the confirm hook (waiver + calendar
 * invite), the activity and the event run only once the server says ok.
 */
export function bookWalkInFuture(input: WalkInFutureInput): WalkInFutureResult {
  const at = nowIso();
  const studioId = studioForWalkIn(input.artistId, input.customerId);
  const resolved = input.customerId
    ? undefined
    : draftWalkInCustomer(input, studioId);
  const customerId = input.customerId ?? resolved!.customer.id;
  const durationMin = Math.max(15, Math.round(input.durationMin));
  const slotMs = new Date(input.startIso).getTime();
  const bookingId = `bk-walkin-future-${customerId}-${slotMs}`;
  const depositId = `dep-walkin-future-${customerId}-${slotMs}`;

  const booking: Booking = {
    id: bookingId,
    customerId,
    studioId,
    artistId: input.artistId,
    start: input.startIso,
    durationMin,
    sessionLength: sessionLengthForDuration(durationMin),
    status: 'confirmed',
    designBrief: 'Walk-in - future booking, deposit taken in shop.',
    referencePhotos: [],
    source: 'in-studio-walkin',
    createdAt: at,
  };

  const settled = createAndPayAtDesk({
    booking,
    depositId,
    amountAud: depositForDuration(durationMin),
    method: input.method,
    channel: WALKIN_METHOD_CHANNEL[input.method],
    // D05-11: the deposit is taken now, so the email captured with the walk-in goes too.
    ...(input.email?.trim() ? { contactEmail: input.email.trim().toLowerCase() } : {}),
    ...(resolved?.isNew ? { customer: resolved.customer } : {}),
    onConfirmed: () => {
      onBookingConfirmed(bookingId);
      useBrainStore.getState().appendActivity({
        id: `act-walkin-future-${bookingId}`,
        type: 'booking-created',
        customerId,
        studioId,
        artistId: input.artistId,
        body: `Walk-in booked a future session with ${input.artistId}; deposit taken in shop.`,
        timestamp: at,
      });
      eventBus.publish('walkin:booked', { bookingId, depositId, method: input.method });
    },
    onFailed: (f) => {
      const copy = intentFailureCopy(f);
      useUIStore.getState().pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 10000 });
    },
  });
  return { bookingId, depositId, settled };
}
